import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { Plug, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { organizationDataAtom, campaignsDataAtom } from '@/store';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { linkCampaignConversionAction, getHubSpotPipelineConfig } from '@/lib/apis';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import {
  useHubSpotIntegrationStatus,
  hubspotPipelinesQueryOptions,
  useSaveHubSpotPipelineConfig,
  useSetupHubSpotConversions,
} from '@/lib/query-options';
import type { ConversionPlatform } from '@/lib/types';

const FUNNEL_STAGES = ['LEAD', 'MQL', 'SQL', 'CUSTOMER'] as const;

// Ad platforms a conversion stage fires to. Only Google Ads is wired up today; Meta lands with Phase 5,
// at which point this becomes a user-selectable multi-select in step 2.
const DEFAULT_DESTINATION_PLATFORMS: ConversionPlatform[] = ['GOOGLE_ADS'];

interface StageConfig {
  name: string;
  value: string;
  funnel_stage: string;
  conversion_type: 'primary' | 'secondary';
}

interface HubSpotFooterState {
  primaryLabel: string;
  primaryDisabled: boolean;
  onPrimary: () => void;
  onBack?: () => void;
  leftContent?: React.ReactNode;
}

interface HubSpotEventsTabProps {
  onFooterChange?: (footer: HubSpotFooterState | null) => void;
  /** When provided, called after conversion actions are created instead of showing step 3 (campaign linking). */
  onCompleted?: (registryIds: number[]) => void;
}

export function HubSpotEventsTab({ onFooterChange, onCompleted }: HubSpotEventsTabProps) {
  const { data: hubspotStatus } = useHubSpotIntegrationStatus();
  const organization = useAtomValue(organizationDataAtom);
  // Success/error shown inline, not via toast (per design system)
  const { symbol } = useCurrencySymbol();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const [selectedStageIds, setSelectedStageIds] = useState<Set<string>>(new Set());
  const [stageConfigs, setStageConfigs] = useState<Record<string, StageConfig>>({});
  const [createdActionIds, setCreatedActionIds] = useState<number[]>([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<Set<number>>(new Set());
  const [isLinking, setIsLinking] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);

  const campaigns = useAtomValue(campaignsDataAtom);

  const orgId = organization?.id;
  const isConnected = hubspotStatus?.is_connected || false;

  const { data: pipelinesData, isLoading: isLoadingPipelines } = useQuery({
    ...hubspotPipelinesQueryOptions(orgId || 0),
    enabled: isConnected && !!orgId,
  });

  // The saved pipeline config tells us which stages already have a Google Ads conversion action.
  const { data: pipelineConfig } = useQuery({
    queryKey: ['hubspot', 'pipeline-config', orgId ?? 0],
    queryFn: () => getHubSpotPipelineConfig(orgId!),
    enabled: isConnected && !!orgId,
  });

  const saveConfigMutation = useSaveHubSpotPipelineConfig();
  const setupConversionsMutation = useSetupHubSpotConversions();

  const pipelines = pipelinesData?.pipelines || [];
  const selectedPipeline = pipelines.find((p) => p.pipeline_id === selectedPipelineId);
  const stages = selectedPipeline?.stages || [];
  const selectedStages = stages.filter((s) => selectedStageIds.has(s.stage_id));

  // stage_id -> existing conversion action name, for the currently-selected pipeline only.
  const existingActionByStageId = new Map<string, string>();
  if (pipelineConfig && pipelineConfig.pipeline_id === selectedPipelineId) {
    for (const s of pipelineConfig.stages ?? []) {
      if (s.is_conversion_event && s.google_ads_conversion_action_id) {
        existingActionByStageId.set(
          s.stage_id,
          s.conversion_action_name || s.google_ads_conversion_action_name || s.stage_label || s.stage_id
        );
      }
    }
  }
  const overwrittenActionNames = [...selectedStageIds]
    .filter((id) => existingActionByStageId.has(id))
    .map((id) => existingActionByStageId.get(id)!);

  // When the user picks the pipeline that already has a saved config, pre-fill the selected stages and
  // their settings from it — so re-running setup updates the existing conversion actions instead of
  // silently dropping other stages' mappings, and so the overwrite warning reflects the real config.
  const prefilledPipelineRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pipelineConfig || !selectedPipelineId || pipelineConfig.pipeline_id !== selectedPipelineId) return;
    if (prefilledPipelineRef.current === selectedPipelineId) return;
    prefilledPipelineRef.current = selectedPipelineId;
    const convStages = (pipelineConfig.stages ?? []).filter((s) => s.is_conversion_event);
    setSelectedStageIds(new Set(convStages.map((s) => s.stage_id)));
    const configs: Record<string, StageConfig> = {};
    convStages.forEach((s, index) => {
      configs[s.stage_id] = {
        name: s.conversion_action_name || s.google_ads_conversion_action_name || `Glitch: ${s.stage_label ?? ''}`,
        value: s.conversion_value != null ? String(s.conversion_value) : '',
        funnel_stage: s.funnel_stage || FUNNEL_STAGES[Math.min(index, FUNNEL_STAGES.length - 1)],
        conversion_type: s.primary_for_goal === false ? 'secondary' : 'primary',
      };
    });
    setStageConfigs(configs);
  }, [pipelineConfig, selectedPipelineId]);

  const toggleStage = (stageId: string) => {
    setSelectedStageIds((prev) => {
      const next = new Set(prev);
      if (next.has(stageId)) {
        next.delete(stageId);
      } else {
        next.add(stageId);
      }
      return next;
    });
  };

  const handlePipelineChange = (value: string) => {
    setSelectedPipelineId(value);
    setSelectedStageIds(new Set());
    setStageConfigs({});
    prefilledPipelineRef.current = null;
    setStep(1);
  };

  const handleNextStep = () => {
    // Keep any pre-filled / previously-entered config; default the rest.
    const configs: Record<string, StageConfig> = {};
    const selected = stages.filter((s) => selectedStageIds.has(s.stage_id));
    selected.forEach((stage, index) => {
      configs[stage.stage_id] = stageConfigs[stage.stage_id] ?? {
        name: `Glitch: ${stage.label}`,
        value: '',
        funnel_stage: FUNNEL_STAGES[Math.min(index, FUNNEL_STAGES.length - 1)],
        conversion_type: 'primary',
      };
    });
    setStageConfigs(configs);
    setStep(2);
  };

  const handleSaveWithGuard = () => {
    if (overwrittenActionNames.length > 0) {
      setShowOverwriteConfirm(true);
      return;
    }
    void handleSave();
  };

  const updateStageConfig = (stageId: string, field: keyof StageConfig, value: string) => {
    setStageConfigs((prev) => ({
      ...prev,
      [stageId]: { ...prev[stageId], [field]: value },
    }));
  };

  const handleSave = async () => {
    if (!orgId || !selectedPipeline) return;
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const config = await saveConfigMutation.mutateAsync({
        organizationId: orgId,
        data: {
          pipeline_id: selectedPipeline.pipeline_id,
          pipeline_label: selectedPipeline.label,
          stages: stages.map((s) => ({
            stage_id: s.stage_id,
            stage_label: s.label,
            stage_order: s.display_order,
            is_conversion_event: selectedStageIds.has(s.stage_id),
            funnel_stage: stageConfigs[s.stage_id]?.funnel_stage || undefined,
            conversion_value: stageConfigs[s.stage_id]?.value
              ? parseFloat(stageConfigs[s.stage_id].value)
              : undefined,
            conversion_action_name: stageConfigs[s.stage_id]?.name?.trim() || undefined,
            primary_for_goal: stageConfigs[s.stage_id]
              ? stageConfigs[s.stage_id].conversion_type === 'primary'
              : undefined,
            platforms: selectedStageIds.has(s.stage_id) ? DEFAULT_DESTINATION_PLATFORMS : [],
          })),
        },
      });

      let registryIds: number[] = [];

      if (selectedStages.length > 0) {
        const setupResult = await setupConversionsMutation.mutateAsync({
          organizationId: orgId,
          configId: config.id,
        });

        // Use registry IDs directly from setup response (no re-fetch needed)
        registryIds = (setupResult?.actions || [])
          .map((a) => a.registry_id)
          .filter((id): id is number => id != null);
        setCreatedActionIds(registryIds);
      }

      setSuccessMessage(`${selectedStages.length} stage${selectedStages.length !== 1 ? 's' : ''} mapped as conversion events`);

      if (onCompleted) {
        // Campaign context: hand registry IDs back to caller for auto-linking
        onCompleted(registryIds);
      } else {
        // Assets context: move to Step 3 for campaign assignment
        const linkableCampaigns = campaigns.filter((c) => c.origin !== 'external');
        if (linkableCampaigns.length > 0) {
          setStep(3);
        } else {
          setStep(1);
        }
      }
    } catch (error) {
      setSuccessMessage('');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save pipeline configuration');
    }
  };

  const toggleCampaign = (campaignId: number) => {
    setSelectedCampaignIds((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  const handleLinkCampaigns = async () => {
    if (selectedCampaignIds.size === 0) return;

    if (createdActionIds.length === 0) {
      setSuccessMessage('Conversion actions already linked to selected campaigns');
      setStep(1);
      setSelectedCampaignIds(new Set());
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setIsLinking(true);
    try {
      let linked = 0;
      for (const campaignId of selectedCampaignIds) {
        for (const actionId of createdActionIds) {
          try {
            await linkCampaignConversionAction(campaignId, actionId);
            linked++;
          } catch (linkError) {
            // Skip duplicate link errors (409/conflict), re-throw real failures
            const msg = linkError instanceof Error ? linkError.message : '';
            if (!msg.includes('409') && !msg.includes('already') && !msg.includes('duplicate')) {
              throw linkError;
            }
          }
        }
      }
      setSuccessMessage(
        linked > 0
          ? `Conversion actions linked to ${selectedCampaignIds.size} campaign${selectedCampaignIds.size !== 1 ? 's' : ''}`
          : 'Conversion actions already linked to selected campaigns'
      );
      setStep(1);
      setSelectedCampaignIds(new Set());
    } catch (error) {
      setSuccessMessage('');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to link campaigns');
    } finally {
      setIsLinking(false);
    }
  };

  const isSaving = saveConfigMutation.isPending || setupConversionsMutation.isPending;

  useEffect(() => {
    if (!onFooterChange) return;
    if (!isConnected || isLoadingPipelines || pipelines.length === 0) {
      onFooterChange(null);
      return;
    }
    if (step === 1) {
      onFooterChange(
        !selectedPipeline ? null : {
          primaryLabel: 'Configure Stages',
          primaryDisabled: selectedStageIds.size === 0,
          onPrimary: handleNextStep,
          leftContent: (
            <p className="text-sm text-gray-600">
              {selectedStageIds.size > 0
                ? `${selectedStageIds.size} stage${selectedStageIds.size !== 1 ? 's' : ''} selected`
                : 'No stages selected'}
            </p>
          ),
        }
      );
    } else if (step === 2) {
      onFooterChange({
        primaryLabel: isSaving ? 'Creating Actions...' : 'Save & Create Actions',
        primaryDisabled: isSaving,
        onPrimary: handleSaveWithGuard,
        onBack: () => setStep(1),
      });
    } else if (step === 3) {
      onFooterChange({
        primaryLabel: isLinking ? 'Linking...' : 'Link to Campaigns',
        primaryDisabled: selectedCampaignIds.size === 0 || isLinking,
        onPrimary: handleLinkCampaigns,
        onBack: () => setStep(2),
        leftContent: (
          <button
            onClick={() => { setStep(1); setSelectedCampaignIds(new Set()); }}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Skip this step
          </button>
        ),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFooterChange, isConnected, isLoadingPipelines, pipelines.length, step, selectedPipeline, selectedStageIds.size, isSaving, selectedStages.length, isLinking, selectedCampaignIds.size, stageConfigs, pipelineConfig, overwrittenActionNames.length]);

  // Not connected
  if (!isConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] px-6 text-center">
        <Plug className="h-12 w-12 text-gray-400 mb-4" strokeWidth={1} />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">HubSpot Not Connected</h3>
        <p className="text-sm text-gray-500 mb-6 max-w-md">
          Connect your HubSpot account to select which pipeline stages should be tracked as conversions for your ad campaigns.
        </p>
        <Button asChild className="bg-[#290BD9] hover:bg-[#2309C5] text-white">
          <Link to="/settings/account" hash="integrations">
            Go to Integrations
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    );
  }

  if (isLoadingPipelines) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px]">
        <Spinner size="xl" className="text-gray-400 mb-4" />
        <p className="text-sm text-gray-500">Loading pipelines from HubSpot...</p>
      </div>
    );
  }

  if (pipelines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] px-6 text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pipelines Found</h3>
        <p className="text-sm text-gray-500">No deal pipelines were found in your connected HubSpot account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Inline feedback */}
      {successMessage && step === 1 && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {errorMessage}
        </div>
      )}

      {/* Step 1: Pipeline + Stage Selection */}
      {step === 1 && (
        <>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">Hubspot Pipeline</label>
            <Select value={selectedPipelineId} onValueChange={handlePipelineChange}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Select a pipeline" />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((pipeline) => (
                  <SelectItem key={pipeline.pipeline_id} value={pipeline.pipeline_id}>
                    {pipeline.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedPipeline && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-900">Hubspot Deal Stages</label>
              <div className="space-y-2 h-[218px] overflow-y-auto">
                {stages.map((stage) => {
                  const isSelected = selectedStageIds.has(stage.stage_id);
                  return (
                    <label
                      key={stage.stage_id}
                      className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleStage(stage.stage_id)}
                        className="rounded border-gray-300"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{stage.label}</p>
                        {existingActionByStageId.has(stage.stage_id) && (
                          <p className="text-xs text-[#A16207] mt-0.5">Already a conversion action</p>
                        )}
                      </div>
                      {isSelected && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

        </>
      )}

      {/* Step 2: Per-Stage Configuration */}
      {step === 2 && (
        <>
          <div className="space-y-3 h-[300px] overflow-y-auto overflow-x-hidden">
            {selectedStages.map((stage) => {
              const config = stageConfigs[stage.stage_id];
              if (!config) return null;

              return (
                <div key={stage.stage_id} className="border rounded-lg p-4 space-y-3">
                  {existingActionByStageId.has(stage.stage_id) && (
                    <div className="flex items-start gap-2 text-xs text-[#A16207] bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-2.5 py-2">
                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
                      <span>
                        Already mapped to the conversion action "{existingActionByStageId.get(stage.stage_id)}".
                        Saving updates that action for every campaign that uses it.
                      </span>
                    </div>
                  )}
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Name</label>
                    <Input
                      value={config.name}
                      onChange={(e) => updateStageConfig(stage.stage_id, 'name', e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs text-gray-500">Funnel Stage</label>
                      <Select
                        value={config.funnel_stage}
                        onValueChange={(v) => updateStageConfig(stage.stage_id, 'funnel_stage', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LEAD">Lead</SelectItem>
                          <SelectItem value="MQL">MQL</SelectItem>
                          <SelectItem value="SQL">SQL</SelectItem>
                          <SelectItem value="CUSTOMER">Customer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs text-gray-500">Conversion Value</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">{symbol}</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Use deal value"
                          value={config.value}
                          onChange={(e) => updateStageConfig(stage.stage_id, 'value', e.target.value)}
                          className="pl-7"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs text-gray-500">Conversion Type</label>
                      <Select
                        value={config.conversion_type}
                        onValueChange={(v) => updateStageConfig(stage.stage_id, 'conversion_type', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="primary">Primary</SelectItem>
                          <SelectItem value="secondary">Secondary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500">
                    Conversions are uploaded to <span className="font-medium text-gray-700">Google Ads</span>.
                  </p>
                </div>
              );
            })}
          </div>

        </>
      )}

      {/* Step 3: Assign to Campaigns */}
      {step === 3 && (
        <>
          <div className="space-y-2 h-[300px] overflow-y-auto">
            {campaigns.filter((c) => c.origin !== 'external').map((campaign) => {
              const isSelected = selectedCampaignIds.has(campaign.id);
              return (
                <label
                  key={campaign.id}
                  className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleCampaign(campaign.id)}
                    className="rounded border-gray-300"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{campaign.name}</p>
                    <p className="text-xs text-gray-500">{campaign.goal || 'No type'}</p>
                  </div>
                  {isSelected && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                </label>
              );
            })}
          </div>

        </>
      )}

      <ConformationModal
        isOpen={showOverwriteConfirm}
        onClose={() => setShowOverwriteConfirm(false)}
        onConfirm={() => {
          setShowOverwriteConfirm(false);
          void handleSave();
        }}
        title={overwrittenActionNames.length === 1 ? 'Update Existing Conversion Action?' : 'Update Existing Conversion Actions?'}
        description={
          overwrittenActionNames.length === 1
            ? `The conversion action "${overwrittenActionNames[0]}" already exists. Saving updates it for every campaign that uses it, rather than creating a new one.`
            : `These conversion actions already exist and will be updated for every campaign that uses them, rather than new ones being created: ${overwrittenActionNames.join(', ')}.`
        }
        confirmText="Update"
        cancelText="Cancel"
      />
    </div>
  );
}
